import clsx from 'clsx'
import findIndex from 'lodash/findIndex'
import React, { FC, forwardRef } from 'react'

import AngleRightIcon from '@Assets/SVG/AngleRight'
import { FormElementProps } from '@Components/FormElements/FormElementProps'
import { BaseFormInput, BaseFormInputProps } from '@Components/FormElements/UI/BaseFormInput'
import { useFormElementState } from '@Components/FormElements/useFormElementState'
import { SVGIcon } from '@Components/Icons'
import { useSplitTest } from '@Globals/SplitTests/Hooks'

import styles from './SelectField.module.scss'

export type SelectFieldOption = {
  key: string
  value: string
}

export type SelectFieldProps = Omit<FormElementProps, 'adornments' | 'size' | 'withoutLabelAnimation'>
& Pick<BaseFormInputProps, 'withoutLabelAnimation'>
& JSX.IntrinsicElements['select']
& {
  allowEmptySelection?: boolean
  options: SelectFieldOption[]
  placeholderOption?: SelectFieldOption
  unwrapped?: boolean
}
export const SelectField: FC<SelectFieldProps> = forwardRef(
  (
    {
      allowEmptySelection,
      withoutLabelAnimation,
      error,
      id,
      label,
      name,
      placeholder,
      placeholderOption,
      variant,
      wrapperClassName,
      size = 1,
      unwrapped,
      options = [],
      value,
      ...props
    }: SelectFieldProps,
    ref,
  ) => {
    const { formActive, ...state } = useFormElementState<'select'>({ ...props })
    const cofDesignV1 = useSplitTest('cof_design_v1', 'b')

    const selectedOptionExists = (findIndex(options, option => option.key === value)) > -1

    const field = (
      <select
        // In case we're in "unwrapped" mode, we should probably make sure the input is still labelled.
        aria-label={unwrapped ? label : undefined}
        id={id}
        name={name}
        ref={ref}
        size={size}
        value={value}
        {...props}
        {...state}
      >
        {(placeholder || placeholderOption) && (
          <option
            disabled={selectedOptionExists && !allowEmptySelection}
            value={placeholderOption ? placeholderOption.key : ''}
          >
            {placeholderOption ? placeholderOption.value : placeholder}
          </option>
        )}
        {options.map((option): JSX.Element => <option key={option.key} value={option.key}>{option.value}</option>)}
      </select>
    )

    if (unwrapped) {
      return field
    }

    return (
      <BaseFormInput
        adornments={{ end: size === 1 && <div className={styles.iconWrapper}><SVGIcon icon={AngleRightIcon} size="15px" /></div> }}
        className={clsx(props.className, cofDesignV1 && styles.selectInput)}
        error={error}
        formActive
        id={id}
        label={label}
        variant={variant}
        withoutLabelAnimation={withoutLabelAnimation}
        wrapperClassName={wrapperClassName}
        {...props}
      >
        {field}
      </BaseFormInput>
    )
  },
)
